import ConsentManager from "../ConsentManager";
import Tag from "./Tag";
import DefaultTag from "./Tag/DefaultTag";
import Toggle from "./Toggle";

/**
 * ConsentManager.Interface
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @lastModified 2024-12-18
 * @package felixkiesewetter-js/consentmanager
 * @since 5.1.0
 */
class Interface
{

    #acceptAll: HTMLButtonElement;
    #acceptSelected: HTMLButtonElement;
    #consentManager :ConsentManager;
    #denyAll: HTMLButtonElement;
    #fingerprintIcon: HTMLButtonElement;
    #form: HTMLFormElement;
    #layout: String;
    #node: HTMLElement;
    #template: String;
    #theme: String;

    /**
     * Interface constructor
     *
     * @param template
     * @param layout
     * @param theme
     * @param translations
     */
    public constructor(consentManager: ConsentManager)
    {
        this.#consentManager = consentManager;
        this.#layout = this.#consentManager.getSetting('interfaceLayout') as String;
        this.#template = this.#consentManager.getSetting('interfaceTemplate') as String;
        this.#theme = this.#consentManager.getSetting('interfaceTheme') as String;

        this.#initNode()
            .#appendContent()
            .#appendForm()
            .#appendActions()
            .#appendFingerprintIcon();

        return;
        // TODO: re-implement
        /* Escape key will dismiss the interface if no interaction is required
        document.addEventListener('keyup', (e) => {
            if ('Escape' === e.key && this.#node.classList.contains('-visible')) {
                if (!this.#requireUserInteraction) {
                    this.hideInterface();
                    return;
                }
                console.warn('ConsentManager interface cannot be dismissed – user interaction is required!');
            }
        });*/
    }

    /**
     * Initialize interface node
     *
     * @private
     */
    #initNode() :Interface {
        this.#node = document.createElement('aside');
        this.#node.classList.add('consent-manager');
        this.#node.classList.add(`consent-manager--${this.#layout}`);
        this.#node.classList.add(`consent-manager--${this.#theme}`);
        const dismiss :HTMLButtonElement = document.createElement('button');
        dismiss.textContent = '×';
        dismiss.classList.add('consent-manager__dismiss');
        dismiss.addEventListener('click', (e) :void => {
            e.preventDefault();
            this.hide();
        });
        this.#node.appendChild(dismiss);
        document.body.appendChild(this.#node);
        return this;
    }

    /**
     * Append content to the interface
     *
     * @private
     */
    #appendContent() :Interface
    {
        const content :HTMLDivElement = document.createElement('div');
        content.classList.add('consent-manager__content');
        content.innerHTML = this.#template;
        for (const key in this.getConsentManager().getTranslator().getAll()) {
            const value = this.getConsentManager().getTranslator().get(key);
            content.innerHTML = content.innerHTML.replace(`{${key}}`, value);
        }
        content.innerHTML = content.innerHTML.replace('{link_privacy}', `<a href="${this.getConsentManager().getPrivacyUri()}" target="_blank">${this.getConsentManager().getTranslator().get('label_privacy_link')}</a>`);
        content.innerHTML = this.getConsentManager().getTranslator().replaceAll(content.innerHTML);
        this.#node.appendChild(content);
        return this;
    }

    /**
     * Append the fingerprint icon.
     */
    #appendFingerprintIcon() :Interface
    {
        if (!this.#consentManager.getSetting('fingerprintIcon')) {
            return;
        }
        this.#fingerprintIcon = document.createElement('button');
        this.#fingerprintIcon.classList.add('consent-manager__fingerprint');
        this.#fingerprintIcon.classList.add(`consent-manager__fingerprint--${this.#consentManager.getSetting('fingerprintIconLayout')}`);
        this.#fingerprintIcon.setAttribute('rel', 'consent-manager');
        this.#fingerprintIcon.setAttribute('aria-label', this.#consentManager.getTranslator().get('title_fingerprint'));
        // TODO: correct aria implementation!
        this.#fingerprintIcon.innerHTML =
            '<svg height="48" viewBox="0 0 48 48" width="48" xmlns="http://www.w3.org/2000/svg">\n' +
            '    <path d="M35.62 8.94c-.16 0-.31-.04-.46-.11C31.33 6.85 28 6 24.02 6c-3.97 0-7.71.95-11.14 2.82-.49.26-1.09.09-1.36-.4-.26-.49-.09-1.09.4-1.36C15.65 5.03 19.72 4 24.02 4c4.26 0 7.98.94 12.06 3.05.49.25.68.86.43 1.35-.18.34-.53.54-.89.54zM7 19.44c-.2 0-.4-.06-.58-.18-.45-.32-.56-.94-.24-1.39 1.98-2.8 4.51-5 7.51-6.55 6.29-3.25 14.33-3.26 20.63-.02 2.99 1.54 5.51 3.72 7.5 6.5.32.45.22 1.07-.23 1.39-.45.32-1.08.22-1.4-.23-1.8-2.52-4.08-4.5-6.78-5.88-5.74-2.95-13.07-2.94-18.8.02-2.71 1.4-5 3.39-6.79 5.93-.2.27-.51.41-.82.41zm12.51 24.13c-.26 0-.51-.1-.71-.3-1.73-1.75-2.67-2.86-4.02-5.27-1.38-2.46-2.11-5.47-2.11-8.69 0-5.94 5.08-10.78 11.33-10.78s11.33 4.83 11.33 10.78c0 .55-.45 1-1 1s-1-.45-1-1c0-4.84-4.18-8.78-9.33-8.78-5.14 0-9.33 3.94-9.33 8.78 0 2.88.64 5.54 1.85 7.71 1.29 2.3 2.15 3.29 3.69 4.84.39.39.39 1.03-.01 1.41-.18.21-.44.3-.69.3zm14.33-3.7c-2.38 0-4.47-.6-6.2-1.77-2.97-2.02-4.75-5.3-4.75-8.78 0-.55.45-1 1-1s1 .45 1 1c0 2.81 1.45 5.47 3.88 7.12 1.41.96 3.07 1.43 5.07 1.43.48 0 1.29-.05 2.09-.19.54-.1 1.06.27 1.16.81.1.54-.27 1.06-.81 1.16-1.17.21-2.16.22-2.44.22zM29.81 44c-.09 0-.18-.01-.26-.04-3.19-.87-5.27-2.05-7.43-4.2-2.79-2.78-4.33-6.49-4.33-10.44 0-3.25 2.76-5.89 6.16-5.89 3.4 0 6.16 2.64 6.16 5.89 0 2.14 1.87 3.89 4.16 3.89s4.16-1.74 4.16-3.89c0-7.54-6.5-13.67-14.49-13.67-5.69 0-10.88 3.16-13.22 8.06-.78 1.62-1.17 3.51-1.17 5.61 0 1.56.14 4.02 1.33 7.21.19.52-.07 1.09-.59 1.29-.52.19-1.09-.07-1.29-.59-.98-2.63-1.46-5.21-1.46-7.91 0-2.4.46-4.58 1.37-6.47 2.67-5.58 8.57-9.19 15.02-9.19 9.09 0 16.49 7.03 16.49 15.67 0 3.25-2.77 5.89-6.16 5.89s-6.16-2.64-6.16-5.89c0-2.14-1.87-3.89-4.16-3.89s-4.16 1.74-4.16 3.89c0 3.41 1.33 6.62 3.74 9.02 1.89 1.88 3.73 2.92 6.55 3.69.53.15.85.7.7 1.23-.12.44-.52.73-.96.73z"/>\n' +
            '</svg>';
        document.body.appendChild(this.#fingerprintIcon);
    }

    /**
     * Append form to the interface
     *
     * @private
     */
    #appendForm() :Interface
    {
        this.#form = document.createElement('form');
        this.#form.classList.add('consent-manager__form');
        this.#node.appendChild(this.#form);
        return this;
    }

    /**
     * Append actions
     *
     * @private
     */
    #appendActions() :Interface
    {
        const actions :HTMLDivElement = document.createElement('div');
        actions.classList.add('consent-manager__actions');
        this.#node.appendChild(actions);

        this.#acceptSelected = document.createElement('button');
        this.#acceptSelected.textContent = this.getConsentManager().getTranslator().get('label_accept_selected');
        this.#acceptSelected.classList.add('consent-manager__action');
        this.#acceptSelected.classList.add('consent-manager__action--accept-selected');
        actions.appendChild(this.#acceptSelected);

        this.#acceptAll = document.createElement('button');
        this.#acceptAll.textContent = this.getConsentManager().getTranslator().get('label_accept_all');
        this.#acceptAll.classList.add('consent-manager__action');
        this.#acceptAll.classList.add('consent-manager__action--accept-all');
        if (!this.getConsentManager().getSetting('useAcceptAll')) {
            this.#acceptAll.classList.add('-disabled');
        }
        actions.appendChild(this.#acceptAll);

        this.#denyAll = document.createElement('button');
        this.#denyAll.textContent = this.getConsentManager().getTranslator().get('label_deny_all');
        this.#denyAll.classList.add('consent-manager__action');
        this.#denyAll.classList.add('consent-manager__action--deny-all');
        if (!this.getConsentManager().getSetting('useDenyAll')) {
            this.#denyAll.classList.add('-disabled');
        }
        actions.appendChild(this.#denyAll);

        return this;
    }

    // #######
    // Methods
    // #######

    /**
     * Add tag
     *
     * @param tag
     */
    public addTag(tag :Tag) :Interface
    {
        const label :HTMLLabelElement = document.createElement('label');
        this.#form.appendChild(label);
        label.classList.add('consent-manager__tag');
        label.classList.add('tag');
        label.dataset.consent = tag.getName();

        const labelAnchor :HTMLAnchorElement = document.createElement('a');
        label.appendChild(labelAnchor);
        labelAnchor.href = tag.constructor === DefaultTag ? this.getConsentManager().getPrivacyUri() : (tag.getUri() || '#');
        labelAnchor.textContent = tag.getLabel();
        labelAnchor.classList.add('tag__label');

        const checkbox :HTMLInputElement = document.createElement('input');
        label.appendChild(checkbox);
        checkbox.classList.add('tag__checkbox');
        checkbox.type = 'checkbox';
        checkbox.name = `allow[${tag.getName()}]`; // TODO: Remove in the future, see ConsentManager.#updateTags()
        label.dataset.consent = tag.getName();
        if (tag.isReadonly()) {
            checkbox.setAttribute('readonly', true);
            checkbox.setAttribute('onclick', 'return false;');
        }
        if (tag.getConsent()) {
            checkbox.setAttribute('checked', true);
        }
        new Toggle(checkbox);

        if (tag.getDescription() || tag.getCategory()) {
            const description :HTMLSpanElement = document.createElement('span');
            description.classList.add('tag__description');
            if (tag.getDescription()) {
                description.textContent = tag.getDescription();
            }
            if (tag.getCategory()) {
                description.innerHTML = `<b class="tag__category">${tag.getCategory()}</b>&nbsp;&middot;&nbsp;${description.textContent}`;
            }
            label.appendChild(description);
        }

        return this;
    }

    /**
     * Hide the interface
     */
    public hide() :Interface
    {
        this.#node.classList.remove('-visible');
        return this;
    }

    /**
     * Highlight a tag with given name
     *
     * @param tagName
     */
    public highlightTagWithName(tagName :String) :Interface
    {
        const tag :HTMLLabelElement = this.#form.querySelector(`.tag[data-consent="${tagName}"]`);
        if (!tag) {
            return this;
        }
        tag.classList.add('-highlighted');
        setTimeout(() :void => {
            tag.classList.remove('-highlighted');
        }, 5000);
        return this;
    }

    public show() :Interface
    {
        this.#node.classList.add('-visible');
        return this;
    }

    // #######
    // Getters
    // #######

    /**
     * Getter for the accept all button
     */
    public getAcceptAll() :HTMLButtonElement
    {
        return this.#acceptAll;
    }

    /**
     * Getter for the accept selected button
     */
    public getAcceptSelected() :HTMLButtonElement
    {
        return this.#acceptSelected;
    }

    /**
     * Getter for the consent manager instance
     */
    public getConsentManager() :ConsentManager
    {
        return this.#consentManager;
    }

    /**
     * Getter for the deny all button
     */
    public getDenyAll() :HTMLButtonElement
    {
        return this.#denyAll;
    }

    /**
     * Getter for the interface node
     */
    public getNode() :HTMLElement
    {
        return this.#node;
    }

}

export default Interface;
